import { useUser } from '@clerk/clerk-react';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { InputTextarea } from 'primereact/inputtextarea';
import { ScrollPanel } from 'primereact/scrollpanel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Markdown from 'react-markdown';
import {
  docCompareChatEndpointPOST,
  DocCompareChatEndpointDef,
} from '~/api/def/endpoints/docCompare/docCompareChatEndpoint';
import {
  DocCompareChunkFinderChunk,
  docCompareChunkFinderEndpointPOST,
} from '~/api/def/endpoints/docCompare/docCompareChunkFinderEndpoint';
import { endpointFundDocumentUrlGET } from '~/api/def/endpoints/document';
import { FundDocument } from '~/api/def/endpoints/fundEndpoint';
import { ChatMessage } from '~/components/ui/misc/ChatMessage';
import { PDFContainer } from '~/components/ui/vendor/pdf/pdf_container';
import { CommentedHighlight } from '~/components/ui/vendor/pdf/pdf_container/types';
import { chunkFinderToPDFHighlights } from '~/lib/pdfViewer/clausesToPDFHighlights';

type DocumentCompareContainerProps = {
  visible: boolean;
  fundName: string;
  userSelectedDocuments: FundDocument[];
};

type ChatTextForm = {
  message: string;
};

type ChatMessage = {
  name: string;
  text: string;
  createdAt: string;
  isSender: boolean;
  doc1results?: DocCompareChunkFinderChunk[];
  doc2results?: DocCompareChunkFinderChunk[];
};

export const DocumentCompareContainer = ({
  fundName,
  userSelectedDocuments,
}: DocumentCompareContainerProps) => {
  const [selectedDocuments, setSelectedDocuments] = useState<
    ((FundDocument & { signedUrl?: string }) | undefined)[]
  >(userSelectedDocuments);
  const scrollPanelRef = useRef<ScrollPanel | null>(null); // Ref to ScrollPanel

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const { user } = useUser();
  const [isChunkFinderAPILoading, setIsChunkFinderAPILoading] = useState(false);
  const [isDocUrlLoading, setIsDocUrlLoading] = useState(true);
  const [pdfHighlightsDocOne, setPdfHighlightsDocOne] = useState<
    CommentedHighlight[]
  >([]);
  const [pdfHighlightsDocTwo, setPdfHighlightsDocTwo] = useState<
    CommentedHighlight[]
  >([]);
  const [pdfActiveHighlightsDocOne, setPdfActiveHighlightsDocOne] =
    useState<CommentedHighlight>();
  const [pdfActiveHighlightsDocTwo, setPdfActiveHighlightsDocTwo] =
    useState<CommentedHighlight>();
  const [latestQuery, setlatestQuery] = useState<string | undefined>();

  const pdfCardContainerRef = useRef<Card>(null);

  const fetchDocumentUrlsDocCompare = useCallback(async () => {
    setIsDocUrlLoading(true);
    const promiseArr = selectedDocuments
      .filter((doc) => doc?.id)
      .map((doc) =>
        doc?.id ? endpointFundDocumentUrlGET(doc.id) : Promise.resolve(null),
      );

    try {
      const urls = await Promise.all(promiseArr);
      const updatedDocuments = selectedDocuments.map((doc, index) => {
        if (!doc?.id) return doc;
        return {
          ...doc,
          signedUrl: urls[index]?.data.presignedUrl || '',
        };
      });
      setSelectedDocuments(updatedDocuments);
      setIsDocUrlLoading(false);
    } catch (error) {
      console.error('Error fetching document URLs:', error);
    }
  }, [selectedDocuments]);

  useEffect(() => {
    fetchDocumentUrlsDocCompare();
  }, []);

  const {
    control,
    reset: resetChatTextForm,
    trigger,
    handleSubmit: handleChatTextSubmit,
    setValue: setFieldValue,
    formState: { errors },
  } = useForm<ChatTextForm>({
    defaultValues: {
      message: '',
    },
  });

  useEffect(() => {
    setTimeout(() => {
      if (scrollPanelRef.current) {
        // Access the ScrollPanel container element
        const scrollPanelElement = scrollPanelRef.current;
        if (scrollPanelElement) {
          // Scroll to the bottom of the container
          const scrollPanelElementDOM = scrollPanelElement.getContent();
          if (scrollPanelElementDOM) {
            scrollPanelElementDOM.scrollTop =
              scrollPanelElementDOM.scrollHeight;
          }
        }
      }
    }, 100);
  }, [chatMessages]);

  const onCompareClick = async () => {
    let resDocChat:
      | DocCompareChatEndpointDef['methods']['POST']['response']
      | undefined;

    setChatMessages((prevMessages) => [
      ...prevMessages,
      {
        name: 'Banqora',
        text: 'Comparing....',
        createdAt: format(new Date(), 'HH:mm'),
        isSender: false,
      },
    ]);

    if (pdfActiveHighlightsDocOne && pdfActiveHighlightsDocTwo) {
      resDocChat = await docCompareChatEndpointPOST({
        context1: pdfActiveHighlightsDocOne.comment || '',
        context2: pdfActiveHighlightsDocTwo.comment || '',
        question: latestQuery || '',
      });
    }

    setChatMessages((prevMessages) => [
      ...prevMessages.slice(0, -1), // Remove the "Searching...." message
      {
        name: 'Banqora',
        text:
          typeof resDocChat?.data === 'string'
            ? resDocChat.data
            : "Sorry, I couldn't find any relevant information.",
        createdAt: format(new Date(), 'HH:mm'),
        isSender: false,
      },
    ]);
  };

  const highlightFinder = (
    chunk: DocCompareChunkFinderChunk,
    pdfActiveHighlights: CommentedHighlight[],
  ) => {
    return pdfActiveHighlights.find((item) => item.id === chunk.id.toString());
  };

  const onChatTextFormSubmit = async (data: ChatTextForm) => {
    setIsChunkFinderAPILoading(true);
    setlatestQuery(data.message);
    setChatMessages((prevMessages) => [
      ...prevMessages,
      {
        name: user?.fullName || 'Guest',
        text: data.message,
        createdAt: format(new Date(), 'HH:mm'),
        isSender: true,
      },
    ]);

    setChatMessages((prevMessages) => [
      ...prevMessages,
      {
        name: 'Banqora',
        text: 'Searching....',
        createdAt: format(new Date(), 'HH:mm'),
        isSender: false,
      },
    ]);

    resetChatTextForm();

    try {
      const resDocOne = await docCompareChunkFinderEndpointPOST({
        query_text: data.message,
        number_of_chunks: 2,
        document_id: selectedDocuments[0]?.id,
        use_hyde: true,
      });

      let resDocTwo = undefined;

      if (resDocOne?.data && resDocOne.data[0]) {
        const resDocTwoPromises = resDocOne.data.map((chunk, index) =>
          docCompareChunkFinderEndpointPOST({
            query_text: '',
            large_query_text: chunk.text,
            number_of_chunks: 1,
            document_id: selectedDocuments[1]?.id,
          }).then((result) => ({ index, data: result?.data })),
        );

        const resDocTwoResults = await Promise.all(resDocTwoPromises);
        resDocTwo = {
          data: resDocTwoResults
            .sort((a, b) => a.index - b.index)
            .flatMap((result) => result.data),
        };
      }

      if (resDocOne?.data && resDocOne.data[0]) {
        setPdfHighlightsDocOne(() => [
          ...chunkFinderToPDFHighlights(resDocOne.data),
        ]);
      }

      if (resDocTwo?.data && resDocTwo.data[0]) {
        setPdfHighlightsDocTwo(() => [
          ...chunkFinderToPDFHighlights(resDocTwo.data),
        ]);
      }

      setChatMessages((prevMessages) => [
        ...prevMessages.slice(0, -1), // Remove the "Searching...." message
        {
          name: 'Banqora',
          text: 'Here are the results',
          createdAt: format(new Date(), 'HH:mm'),
          isSender: false,
          doc1results: resDocOne?.data || undefined,
          doc2results:
            resDocTwo?.data?.filter(
              (item): item is DocCompareChunkFinderChunk => item !== undefined,
            ) || undefined,
        },
      ]);
    } catch (err) {
      console.log(err);
    } finally {
      setIsChunkFinderAPILoading(false);
    }
  };
  if (isDocUrlLoading) {
    return <div>loading</div>;
  } else
    return (
      <div className="flex flex-column  h-full">
        <div className="grid">
          <div className="col-9 flex">
            <div className="col-6">
              <Card
                className="border-round-xl shadow-none"
                style={{
                  border: '1px solid lightgrey',
                }}
                pt={{
                  body: { className: 'p-2' },
                  content: { className: 'p-0' },
                }}
              >
                <>
                  <div className="flex flex-column">
                    <div
                      className="text-sm m-0"
                      style={{ color: 'var(--bq-color-black)' }}
                    >
                      Document Name
                    </div>
                    <div
                      className="text-base font-medium m-0"
                      style={{ color: 'var(--bq-color-gray)' }}
                    >
                      {selectedDocuments[0]?.originalName || '-'}
                    </div>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <div>
                      <div
                        className="text-sm m-0"
                        style={{ color: 'var(--bq-color-black)' }}
                      >
                        Agreement Type
                      </div>
                      <div
                        className="text-sm font-medium m-0"
                        style={{ color: 'var(--bq-color-gray)' }}
                      >
                        {selectedDocuments[0]?.type || '-'}
                      </div>
                    </div>
                    {fundName && (
                      <div>
                        <div
                          className="text-sm m-0"
                          style={{ color: 'var(--bq-color-black)' }}
                        >
                          Fund
                        </div>
                        <div
                          className="text-sm font-medium m-0"
                          style={{ color: 'var(--bq-color-gray)' }}
                        >
                          {fundName || '-'}
                        </div>
                      </div>
                    )}
                    <div>
                      <div
                        className="text-sm m-0"
                        style={{ color: 'var(--bq-color-black)' }}
                      >
                        Admission Date
                      </div>
                      <div
                        className="text-sm font-medium m-0"
                        style={{ color: 'var(--bq-color-gray)' }}
                      >
                        {selectedDocuments[0]?.admissionDate?.toDateString() ||
                          '-'}
                      </div>
                    </div>
                  </div>
                </>
              </Card>
            </div>

            <div className="col-6">
              <Card
                className="border-round-xl shadow-none"
                style={{
                  border: '1px solid lightgrey',
                }}
                pt={{
                  body: { className: 'p-2' },
                  content: { className: 'p-0' },
                }}
              >
                <>
                  <div className="flex flex-column">
                    <div
                      className="text-sm m-0"
                      style={{ color: 'var(--bq-color-black)' }}
                    >
                      Document Name
                    </div>
                    <div
                      className="text-base font-medium m-0"
                      style={{ color: 'var(--bq-color-gray)' }}
                    >
                      {selectedDocuments[1]?.originalName || '-'}
                    </div>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <div>
                      <div
                        className="text-sm m-0"
                        style={{ color: 'var(--bq-color-black)' }}
                      >
                        Agreement Type
                      </div>
                      <div
                        className="text-sm font-medium m-0"
                        style={{ color: 'var(--bq-color-gray)' }}
                      >
                        {selectedDocuments[1]?.type || '-'}
                      </div>
                    </div>
                    {fundName && (
                      <div>
                        <div
                          className="text-sm m-0"
                          style={{ color: 'var(--bq-color-black)' }}
                        >
                          Fund
                        </div>
                        <div
                          className="text-sm font-medium m-0"
                          style={{ color: 'var(--bq-color-gray)' }}
                        >
                          {fundName || '-'}
                        </div>
                      </div>
                    )}
                    <div>
                      <div
                        className="text-sm m-0"
                        style={{ color: 'var(--bq-color-black)' }}
                      >
                        Admission Date
                      </div>
                      <div
                        className="text-sm font-medium m-0"
                        style={{ color: 'var(--bq-color-gray)' }}
                      >
                        {selectedDocuments[1]?.admissionDate?.toDateString()}
                      </div>
                    </div>
                  </div>
                </>
              </Card>
            </div>
          </div>
        </div>
        <Divider />

        <div className="grid" style={{ height: 'calc(100vh - 14.5rem)' }}>
          <div className="col-9 flex">
            <div className="col-6">
              <Card
                ref={pdfCardContainerRef}
                className="border-round-xl shadow-none"
                style={{
                  border: '1px solid lightgrey',
                }}
                pt={{
                  root: { className: 'h-full' },
                  body: { className: 'p-2 h-full' },
                  content: { className: 'p-0 h-full' },
                }}
              >
                <>
                  <PDFContainer
                    url={selectedDocuments[0]?.signedUrl || ''}
                    highlights={pdfHighlightsDocOne}
                    activeHighlight={pdfActiveHighlightsDocOne?.id || null}
                    enableAreaSelection={function (
                      _event: MouseEvent,
                    ): boolean {
                      throw new Error('Function not implemented.');
                    }}
                  />
                </>
              </Card>
            </div>
            <div className="col-6">
              <Card
                className="border-round-xl shadow-none"
                style={{
                  border: '1px solid lightgrey',
                }}
                pt={{
                  root: { className: 'h-full' },
                  body: { className: 'p-2 h-full' },
                  content: { className: 'p-0 h-full' },
                }}
              >
                <PDFContainer
                  url={selectedDocuments[1]?.signedUrl || ''}
                  highlights={pdfHighlightsDocTwo}
                  activeHighlight={pdfActiveHighlightsDocTwo?.id || null}
                  enableAreaSelection={function (_event: MouseEvent): boolean {
                    throw new Error('Function not implemented.');
                  }}
                />
              </Card>
            </div>
          </div>
          <div className="col-3 py-3" style={{ height: '100%' }}>
            <Card
              className="border-round-xl shadow-none"
              pt={{
                root: { className: 'h-full' },
                body: { className: 'p-2 h-full' },
                content: { className: 'p-0 h-full' },
              }}
            >
              <div className="flex justify-content-between flex-column h-full">
                <ScrollPanel
                  ref={scrollPanelRef}
                  style={{ height: 'calc(100vh - 30.5rem)' }}
                >
                  <div className="flex flex-column gap-3">
                    {chatMessages.map((msg, index) => (
                      <ChatMessage
                        key={index}
                        name={msg.name}
                        createdAt={msg.createdAt}
                        isSender={msg.isSender}
                      >
                        {Boolean(msg.doc1results && msg.doc2results) ? (
                          <div>
                            Click the text to compare
                            <div className="mt-3" />
                            <div className="grid">
                              <div className="col-12">
                                <table className="w-full">
                                  <thead>
                                    <tr>
                                      <th>Left Document</th>
                                      <th>Right Document</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {msg.doc1results?.map((doc1, index) => (
                                      <tr key={index}>
                                        <td>
                                          <a
                                            href="#"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              setPdfActiveHighlightsDocOne(
                                                highlightFinder(
                                                  doc1,
                                                  pdfHighlightsDocOne,
                                                ),
                                              );
                                            }}
                                          >
                                            p{doc1.bbox_page_start}:{' '}
                                            {doc1.text.slice(0, 30) + '...'}
                                          </a>
                                        </td>
                                        <td>
                                          {msg.doc2results &&
                                            msg.doc2results[index] && (
                                              <a
                                                href="#"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  msg.doc2results &&
                                                    setPdfActiveHighlightsDocTwo(
                                                      highlightFinder(
                                                        msg.doc2results[index],
                                                        pdfHighlightsDocTwo,
                                                      ),
                                                    );
                                                }}
                                              >
                                                p
                                                {
                                                  msg.doc2results[index]
                                                    .bbox_page_start
                                                }
                                                :{' '}
                                                {msg.doc2results[
                                                  index
                                                ].text.slice(0, 30) + '...'}
                                              </a>
                                            )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            <Button
                              label="Compare"
                              type="submit"
                              icon="pi pi-send"
                              className="mt-1"
                              onClick={onCompareClick}
                              disabled={
                                !!errors.message ||
                                !Boolean(
                                  pdfActiveHighlightsDocOne &&
                                    pdfActiveHighlightsDocTwo,
                                )
                              }
                            />
                          </div>
                        ) : (
                          <Markdown>{msg.text}</Markdown>
                        )}
                      </ChatMessage>
                    ))}
                  </div>
                </ScrollPanel>
                <Divider />
                <div>
                  <form onSubmit={handleChatTextSubmit(onChatTextFormSubmit)}>
                    <div>
                      <Controller
                        name="message"
                        control={control}
                        rules={{ required: 'Message is required.' }}
                        render={({ field }) => (
                          <InputTextarea
                            className="w-full border-round-xl"
                            value={field.value}
                            invalid={!!errors.message}
                            rows={6}
                            onChange={(e) => {
                              setFieldValue('message', e.target.value);
                              trigger('message');
                            }}
                            placeholder="How can I help you?"
                          />
                        )}
                      />
                      {errors.message && (
                        <small className="p-error">
                          {errors.message.message}
                        </small>
                      )}
                    </div>
                    <Button
                      label="Send"
                      type="submit"
                      icon="pi pi-send"
                      className="mt-1"
                      loading={isChunkFinderAPILoading}
                      disabled={!!errors.message || isChunkFinderAPILoading}
                    />
                  </form>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
};
